/* eslint-disable complexity */
/* eslint-disable max-statements */
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';

import {
  CURRENT_SLIDE_INDEX_ACTION_PROP,
  EDITING_TYPES,
} from '../../../enums/lessons';
import {LOCKED_SLIDE_TYPES} from '../../../utils/commandPalette';
import * as slideUtils from './slideUtills';
import {
  addClientSideIdToOverlayElements,
  cleanQuestionOptionsByType,
  convertToInteractionWithMixedQuizType,
  createMixedQuizMultipleChoiceQuestion,
  deleteExcludedKeysFromDuplicatedSlide,
  emptyQuestion,
  fromEsonToState,
  getActiveSlide,
  getActiveSlideIndex,
  getFirstSlideElementIndexWithSlideIndex,
  isValidActivity,
  isValidEnd,
  isValidIntro,
  isValidLearningOutcomes,
  isValidResults,
  iterator,
  normaliseOverlayElementOrder,
  setOverlayElementToTop,
  validateSlideByType,
} from './stateUtils';

import colourStyles from '../../../../resources/styles/colours.module.scss';

export const actionTypes = {
  SET_ACTIVE_SLIDE: 'SET_ACTIVE_SLIDE',
  SET_ACTIVE_SLIDE_ELEMENT: 'SET_ACTIVE_SLIDE_ELEMENT',
  MOVE_TO_NEXT_SLIDE_ELEMENT: 'MOVE_TO_NEXT_SLIDE_ELEMENT',
  MOVE_TO_PREVIOUS_SLIDE_ELEMENT: 'MOVE_TO_PREVIOUS_SLIDE_ELEMENT',
  ADD_INTRO: 'ADD_INTRO',
  ADD_LEARNING_OUTCOMES: 'ADD_LEARNING_OUTCOMES',
  ADD_SLIDE: 'ADD_SLIDE',
  ADD_IMAGE_SLIDE: 'ADD_IMAGE_SLIDE',
  ADD_VIDEO_SLIDE: 'ADD_VIDEO_SLIDE',
  ADD_IMAGE_QUIZ: 'ADD_IMAGE_QUIZ',
  ADD_VIDEO_QUIZ: 'ADD_VIDEO_QUIZ',
  SHOW_DELETE_WARNING: 'SHOW_DELETE_WARNING',
  HIDE_DELETE_WARNING: 'HIDE_DELETE_WARNING',
  DELETE_SLIDE: 'DELETE_SLIDE',
  REORDER_SLIDES: 'REORDER_SLIDES',
  EDIT_END: 'EDIT_END',
  EDIT_INTRO: 'EDIT_INTRO',
  EDIT_LEARNING_OUTCOMES: 'EDIT_LEARNING_OUTCOMES',
  EDIT_TEXT: 'EDIT_TEXT',
  EDIT_IMAGE_SLIDE: 'EDIT_IMAGE_SLIDE',
  EDIT_TASKBOX: 'EDIT_TASKBOX',
  EDIT_VIDEO: 'EDIT_VIDEO',
  EDIT_ORDERING_TASK: 'EDIT_ORDERING_TASK',
  EDIT_QUESTION: 'EDIT_QUESTION',
  EDIT_QUESTION_IMAGE: 'EDIT_QUESTION_IMAGE',
  TOGGLE_SHUFFLE_ANSWERS: 'TOGGLE_SHUFFLE_ANSWERS',
  SET_QUIZ_TYPE: 'SET_QUIZ_TYPE',
  SET_ORDERING_INTERACTION: 'SET_ORDERING_INTERACTION',
  SET_QUESTION_TYPE: 'SET_QUESTION_TYPE',
  ADD_QUESTION: 'ADD_QUESTION',
  REORDER_QUESTIONS: 'REORDER_QUESTIONS',
  SHOW_DELETE_QUESTION_WARNING: 'SHOW_DELETE_QUESTION_WARNING',
  HIDE_DELETE_QUESTION_WARNING: 'HIDE_DELETE_QUESTION_WARNING',
  DELETE_QUESTION: 'DELETE_QUESTION',
  EDIT_RESULTS_SLIDE: 'EDIT_RESULTS_SLIDE',
  SET_MINIMUM_SCORE: 'SET_MINIMUM SCORE',
  SET_EDITING_PASS_FAIL: 'SET_EDITING_PASS_FAIL',
  CHANGES_SUBMITTED: 'CHANGES_SUBMITTED',
  PASTE_SLIDE: 'PASTE_SLIDE',
  DUPLICATE_SLIDE: 'DUPLICATE_SLIDE',
  UPDATE_ESON: 'UPDATE_ESON',
  MOVE_OVERLAY_ELEMENT: 'MOVE_OVERLAY_ELEMENT',
  REMOVE_OVERLAY_ELEMENT: 'REMOVE_OVERLAY_ELEMENT',
  REORDER_OVERLAY_ELEMENT: 'REORDER_OVERLAY_ELEMENT',
  REPLACE_SLIDES: 'REPLACE_SLIDES',
};

export const initState = (lesson) => ({
  ...fromEsonToState(lesson),
  activeSlideElementIndex: 0,
  deleteSlideIndex: -1,
  deleteQuestionIndex: -1,
});

const actionTypesToEmptyActivity = {
  [actionTypes.ADD_INTRO]: {
    type: 'intro',
    params: {
      title: '',
      description: '',
      background: '#FFFFFF',
      image: null,
    },
  },
  [actionTypes.ADD_LEARNING_OUTCOMES]: {
    type: 'learningOutcomes',
    params: {
      title: '',
      outcomes: [''],
      image: null,
      cheer: '',
      background: '#FFFFFF',
    },
  },
  [actionTypes.ADD_SLIDE]: {
    type: 'text',
    params: {
      title: '',
      description: '',
      background: '#FFFFFF',
      image: null,
    },
  },
  [actionTypes.ADD_IMAGE_SLIDE]: {
    type: 'imageSlide',
    params: {
      altText: '',
      background: colourStyles.brandPrimaryDark,
      image: {url: ''},
      isLightButtonStyle: true,
    },
    overlayElements: [],
  },
  [actionTypes.ADD_THIRD_PARTY_SLIDE]: {
    type: 'thirdParty',
    params: {
      title: '',
      description: '',
      url: '',
      background: '#FFFFFF',
      minTime: null,
    },
  },
  [actionTypes.ADD_VIDEO_SLIDE]: {
    type: 'video',
    params: {
      url: '',
    },
  },
  [actionTypes.ADD_IMAGE_QUIZ]: {
    type: 'interactive',
    params: {
      interaction: {type: 'imageQuiz', params: {}},
    },
  },
  [actionTypes.ADD_VIDEO_QUIZ]: {
    type: 'interactive',
    params: {
      // Note: that type gets changed to 'ordering' if that's chosen at the next stage
      interaction: {type: 'standaloneQuiz', params: {}},
    },
  },
};

export const reducer = (state, action) => {
  const getSlideElements = slideUtils.getSlideElements(
    state.editingType,
    state.isAssessment
  );

  const hasFullEditingType = state.editingType === EDITING_TYPES.FULL;

  switch (action.type) {
    case actionTypes.SET_ACTIVE_SLIDE:
      return {
        ...state,
        activeSlideElementIndex: getFirstSlideElementIndexWithSlideIndex(
          state.slideElements,
          action.payload
        ),
        deleteSlideIndex: -1,
        deleteQuestionIndex: -1,
      };
    case actionTypes.SET_ACTIVE_SLIDE_ELEMENT:
      return {
        ...state,
        activeSlideElementIndex: action.payload,
        deleteSlideIndex: -1,
        deleteQuestionIndex: -1,
      };
    case actionTypes.DUPLICATE_SLIDE: {
      const activeSlideIndex = getActiveSlideIndex(
        state.slideElements,
        state.activeSlideElementIndex
      );

      if (LOCKED_SLIDE_TYPES.includes(state.slides[activeSlideIndex].type)) {
        return state;
      }
      if (state.isAssessment || !hasFullEditingType) {
        return state;
      }

      const newState = cloneDeep(state);
      const clonedSlide = cloneDeep(newState.slides[activeSlideIndex]);
      deleteExcludedKeysFromDuplicatedSlide(clonedSlide);

      if (clonedSlide.overlayElements) {
        clonedSlide.overlayElements = addClientSideIdToOverlayElements(
          clonedSlide.overlayElements
        );
      }

      const newSlide = {
        ...clonedSlide,
        id: iterator.next().value,
      };

      const newSlideIndex = activeSlideIndex + 1;

      newState.slides.splice(newSlideIndex, 0, newSlide);
      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.slideReadyStates.splice(
        newSlideIndex,
        0,
        isValidActivity(newSlide)
      );
      newState.activeSlideElementIndex = newState.slideElements.findIndex(
        (el) => el.slideIndex === newSlideIndex
      );
      newState.unsavedChanges = true;
      return newState;
    }
    case actionTypes.PASTE_SLIDE: {
      if (state.isAssessment || !hasFullEditingType) {
        return state;
      }
      const newState = cloneDeep(state);
      const clonedSlide = cloneDeep(action.payload);
      deleteExcludedKeysFromDuplicatedSlide(clonedSlide);
      const newSlide = {
        ...clonedSlide,
        id: iterator.next().value,
      };

      const activeSlideIndex = getActiveSlideIndex(
        state.slideElements,
        state.activeSlideElementIndex
      );

      // Prevent pasting after the final slide
      const newSlideIndex = Math.min(
        activeSlideIndex + 1,
        newState.slides.length - 1
      );
      newState.slides.splice(newSlideIndex, 0, newSlide);
      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.slideReadyStates.splice(
        newSlideIndex,
        0,
        isValidActivity(newSlide)
      );
      newState.activeSlideElementIndex = newState.slideElements.findIndex(
        (el) => el.slideIndex === newSlideIndex
      );
      newState.unsavedChanges = true;
      return newState;
    }

    case actionTypes.ADD_INTRO:
    case actionTypes.ADD_LEARNING_OUTCOMES:
    case actionTypes.ADD_SLIDE:
    case actionTypes.ADD_IMAGE_SLIDE:
    case actionTypes.ADD_THIRD_PARTY_SLIDE:
    case actionTypes.ADD_VIDEO_SLIDE:
    case actionTypes.ADD_IMAGE_QUIZ:
    case actionTypes.ADD_VIDEO_QUIZ: {
      const getNewSlideIndex = () => {
        switch (action.type) {
          case actionTypes.ADD_INTRO:
            return 0;
          case actionTypes.ADD_LEARNING_OUTCOMES: {
            const hasIntro =
              state.slides.length > 0 && state.slides[0].type === 'intro';
            return hasIntro ? 1 : 0;
          }
          default:
            return state.slides.length - 1;
        }
      };
      const newSlideIndex = getNewSlideIndex();
      const newState = cloneDeep(state);
      const newSlide = {
        ...cloneDeep(actionTypesToEmptyActivity[action.type]),
        id: iterator.next().value,
      };
      newState.slides.splice(newSlideIndex, 0, newSlide);
      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.slideReadyStates.splice(newSlideIndex, 0, false);
      newState.activeSlideElementIndex = newState.slideElements.findIndex(
        (el) => el.slideIndex === newSlideIndex
      );
      newState.deleteSlideIndex = -1;
      newState.deleteQuestionIndex = -1;
      newState.unsavedChanges = true;
      return newState;
    }
    case actionTypes.SHOW_DELETE_WARNING:
      return {
        ...state,
        deleteSlideIndex: action.payload,
      };
    case actionTypes.MOVE_TO_NEXT_SLIDE_ELEMENT: {
      const nextSlideIndex = state.slideElements.findIndex(
        (el, idx) =>
          idx > state.activeSlideElementIndex && el.name !== 'addQuestion'
      );

      return {
        ...state,
        activeSlideElementIndex:
          nextSlideIndex === -1
            ? state.activeSlideElementIndex
            : nextSlideIndex,
      };
    }
    case actionTypes.MOVE_TO_PREVIOUS_SLIDE_ELEMENT: {
      const prevSlideIndex = state.slideElements.findLastIndex(
        (el, idx) =>
          idx < state.activeSlideElementIndex && el.name !== 'addQuestion'
      );

      return {
        ...state,
        activeSlideElementIndex:
          prevSlideIndex === -1
            ? state.activeSlideElementIndex
            : prevSlideIndex,
      };
    }
    case actionTypes.HIDE_DELETE_WARNING:
      return {
        ...state,
        deleteSlideIndex: -1,
      };
    case actionTypes.DELETE_SLIDE: {
      const activeSlideIndex = getActiveSlideIndex(
        state.slideElements,
        state.activeSlideElementIndex
      );

      const slide = state.slides[activeSlideIndex];

      if (slide.type === 'end' || state.isAssessment || !hasFullEditingType) {
        return state;
      }

      const removeSlideAtIndex = (_slide, index) => {
        if (state.deleteSlideIndex === -1) {
          return index !== activeSlideIndex;
        } else {
          return index !== state.deleteSlideIndex;
        }
      };

      const newState = cloneDeep(state);

      newState.slides = newState.slides.filter(removeSlideAtIndex);
      newState.slideElements = newState.slides.map(getSlideElements).flat();
      if (activeSlideIndex >= state.deleteSlideIndex) {
        const newSlideIndex = activeSlideIndex > 0 ? activeSlideIndex - 1 : 0;
        newState.activeSlideElementIndex =
          getFirstSlideElementIndexWithSlideIndex(
            newState.slideElements,
            newSlideIndex
          );
      }
      newState.slideReadyStates =
        newState.slideReadyStates.filter(removeSlideAtIndex);
      newState.unsavedChanges = true;
      newState.deleteSlideIndex = -1;

      return newState;
    }
    case actionTypes.REORDER_SLIDES: {
      const newState = cloneDeep(state);
      const {prevIndex, newIndex} = action.payload;
      const [slide] = newState.slides.splice(prevIndex, 1);
      const [slideReadyState] = newState.slideReadyStates.splice(prevIndex, 1);
      const slideIndex = getActiveSlideIndex(
        state.slideElements,
        state.activeSlideElementIndex
      );
      const activeSlideId = state.slides[slideIndex].id;

      const indexOfFirstElementOfActiveSlide = state.slideElements.findIndex(
        (element) => element.slideIndex === slideIndex
      );
      const elementIndex =
        state.activeSlideElementIndex - indexOfFirstElementOfActiveSlide;

      newState.slides.splice(newIndex, 0, slide);
      newState.slideReadyStates.splice(newIndex, 0, slideReadyState);
      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.unsavedChanges = true;

      const indexOfFirstElementOfPrevSlide = newState.slideElements.findIndex(
        (el) => el.ephemeralClientSideSlideId === activeSlideId
      );
      newState.activeSlideElementIndex =
        indexOfFirstElementOfPrevSlide + elementIndex;

      return newState;
    }
    case actionTypes.EDIT_END: {
      const newState = cloneDeep(state);

      const i = state.slides.findIndex((s) => s.type === 'end');

      const params = merge(newState.slides[i].params, action.payload);

      newState.slides[i].params = params;
      newState.slideReadyStates[i] = isValidEnd(params);
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.REORDER_QUESTIONS: {
      const newState = cloneDeep(state);
      const {ephemeralClientSideSlideId, prevIndex, newIndex} = action.payload;
      const quizSlide = newState.slides.find(
        (slide) => slide.id === ephemeralClientSideSlideId
      );
      const quiz = quizSlide.params.interaction.params;

      const reorderArray = (arr) => {
        const [movedItem] = arr.splice(prevIndex, 1);
        arr.splice(newIndex, 0, movedItem);
      };

      const quizDataKeysToReorder = [
        'questions',
        'urls',
        'thumbnailUrls',
        'urlsCkey',
        'thumbnailUrlsCkey',
      ];
      quizDataKeysToReorder.forEach((key) => {
        if (quiz[key]) {
          reorderArray(quiz[key]);
        }
      });

      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.unsavedChanges = true;

      const initialSlideElements = state.slideElements
        .map((element, index) => ({...element, slideElementIndex: index}))
        .filter(
          (element) =>
            element.ephemeralClientSideSlideId === ephemeralClientSideSlideId &&
            element.name === 'question'
        );

      const quizSlideElementIndexes = [
        ...initialSlideElements.map((el) => el.slideElementIndex),
      ];

      const isActiveElementIndex = (index) =>
        index === state.activeSlideElementIndex;
      if (quizSlideElementIndexes.some(isActiveElementIndex)) {
        const prevActiveQuestionIndex =
          quizSlideElementIndexes.findIndex(isActiveElementIndex);
        reorderArray(quizSlideElementIndexes);
        const newActiveQuestionIndex =
          quizSlideElementIndexes.findIndex(isActiveElementIndex);
        newState.activeSlideElementIndex +=
          newActiveQuestionIndex - prevActiveQuestionIndex;
      }

      return newState;
    }
    case actionTypes.EDIT_INTRO: {
      const newState = cloneDeep(state);

      const {[CURRENT_SLIDE_INDEX_ACTION_PROP]: _, ...rest} = action.payload;

      //we use merge because rest might contain objects such as button: {text: 'Next', textCKey: 'theKey'}
      const params = merge(newState.slides[0].params, rest);

      newState.slides[0].params = params;
      newState.slideReadyStates[0] = isValidIntro(params);
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.EDIT_LEARNING_OUTCOMES: {
      const newState = cloneDeep(state);

      const i = state.slides.findIndex((s) => s.type === 'learningOutcomes');

      const params = {
        ...newState.slides[i].params,
        ...action.payload,
      };

      newState.slides[i].params = params;
      newState.slideReadyStates[i] = isValidLearningOutcomes(params);
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.EDIT_TEXT: {
      const newState = cloneDeep(state);
      const {[CURRENT_SLIDE_INDEX_ACTION_PROP]: currentSlideIndex, ...rest} =
        action.payload;
      const activeSlideIndex =
        currentSlideIndex ||
        getActiveSlideIndex(state.slideElements, state.activeSlideElementIndex);

      const params = merge(newState.slides[activeSlideIndex].params, rest);

      newState.slides[activeSlideIndex].params = params;
      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.slideReadyStates[activeSlideIndex] = isValidActivity(
        newState.slides[activeSlideIndex]
      );
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.EDIT_IMAGE_SLIDE: {
      const newState = cloneDeep(state);
      const {params = {}, overlay} = action.payload || {};
      const {[CURRENT_SLIDE_INDEX_ACTION_PROP]: _, ...restParams} = params;
      const activeSlideIndex =
        params?.[CURRENT_SLIDE_INDEX_ACTION_PROP] ||
        getActiveSlideIndex(state.slideElements, state.activeSlideElementIndex);
      const activeSlide = newState.slides[activeSlideIndex];

      if (Object.keys(restParams).length) {
        activeSlide.params = merge(activeSlide.params, restParams);
      }

      if (overlay) {
        activeSlide.overlayElements = overlay;
      }

      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.slideReadyStates[activeSlideIndex] =
        isValidActivity(activeSlide);
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.SET_QUIZ_TYPE: {
      const newState = cloneDeep(state);
      const {activeSlide} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );

      const quiz = activeSlide.params;
      const imageUrl =
        quiz.interaction.type === 'imageQuiz'
          ? {urls: [''], thumbnailUrls: ['']}
          : {};

      const {quizType, taskBoxTitle, taskBoxText, prompt} = action.payload;

      quiz.background = colourStyles.brandPrimaryDark;
      quiz.taskBoxTitle = taskBoxTitle;
      quiz.taskBoxText = taskBoxText;
      quiz.interaction.params = {
        quizType: quizType,
        shuffleAnswers: true,
        ...imageUrl,
        ...(quizType !== 'mixed'
          ? {prompt, questions: [emptyQuestion]}
          : {questions: [createMixedQuizMultipleChoiceQuestion(prompt)]}),
      };

      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.SET_QUESTION_TYPE: {
      const newState = cloneDeep(state);
      const {activeSlide, activeSlideIndex} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );

      const {questionIndex, type, defaultPromptMap} = action.payload;

      if (activeSlide.params.interaction.params.quizType !== 'mixed') {
        activeSlide.params.interaction = convertToInteractionWithMixedQuizType(
          activeSlide.params.interaction
        );
      }

      const question =
        activeSlide.params.interaction.params.questions[questionIndex];
      if (
        defaultPromptMap.get(question.type) === question.prompt &&
        defaultPromptMap.get(type)
      ) {
        question.prompt = defaultPromptMap.get(type);
      }

      question.type = type;

      question.options = cleanQuestionOptionsByType(type, question.options);

      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.slideReadyStates[activeSlideIndex] = isValidActivity(
        newState.slides[activeSlideIndex]
      );
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.SET_ORDERING_INTERACTION: {
      const newState = cloneDeep(state);
      const {activeSlide} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );

      const quiz = activeSlide.params;

      const {taskBoxTitle, taskBoxText, prompt} = action.payload;

      quiz.background = colourStyles.brandPrimaryDark;
      quiz.taskBoxTitle = taskBoxTitle;
      quiz.taskBoxText = taskBoxText;
      quiz.interaction.type = 'ordering';
      quiz.interaction.params = {
        quizType: 'text',
        prompt: prompt,
        answers: ['', '', ''],
        questionId: null,
      };

      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.EDIT_TASKBOX: {
      const newState = cloneDeep(state);
      const {activeSlide, activeSlideIndex} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );

      const quiz = activeSlide.params;
      quiz.taskBoxTitle = action.payload.title;
      quiz.taskBoxText = action.payload.text;
      quiz.fontColour = action.payload.fontColour;

      if (typeof action.payload.isLightButtonStyle === 'boolean') {
        quiz.isLightButtonStyle = action.payload.isLightButtonStyle;
      }

      if (action.payload.background) {
        quiz.background = action.payload.background;
      }

      if (action.payload.button) {
        quiz.taskBoxButton = {
          ...quiz.taskBoxButton,
          ...action.payload.button,
        };
      }
      newState.slideReadyStates[activeSlideIndex] =
        isValidActivity(activeSlide);
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.EDIT_VIDEO: {
      const newState = cloneDeep(state);
      const {activeSlide, activeSlideIndex} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );
      if (activeSlide.type === 'video') {
        activeSlide.params = action.payload;
      } else {
        activeSlide.params.video = action.payload;
      }
      newState.slideReadyStates[activeSlideIndex] =
        isValidActivity(activeSlide);
      newState.unsavedChanges = true;
      return newState;
    }
    case actionTypes.EDIT_ORDERING_TASK: {
      const newState = cloneDeep(state);
      const {activeSlide, activeSlideIndex} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );

      const orderingTask = activeSlide.params.interaction.params;
      orderingTask.prompt = action.payload.prompt;
      orderingTask.answers = action.payload.answers;
      orderingTask.answersCkey = action.payload.answersCkey;

      if (action.payload.button) {
        orderingTask.progressButton = action.payload.button;
      }

      newState.slideReadyStates[activeSlideIndex] =
        isValidActivity(activeSlide);
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.EDIT_QUESTION: {
      const newState = cloneDeep(state);
      const {activeSlide, activeSlideIndex} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );

      const {questionIndex, prompt, button, ...questionParams} = action.payload;

      const quiz = activeSlide.params.interaction.params;
      const hasVideoFeedback = questionParams.options.some(
        (option) => option?.videoFeedback?.url
      );
      const isMixedQuizType = quiz.quizType === 'mixed';

      if (!isMixedQuizType) {
        quiz.prompt = prompt;
      }
      quiz.questions[questionIndex] = {
        ...quiz.questions[questionIndex],
        ...questionParams,
        ...(isMixedQuizType && {prompt}),
        hasVideoFeedback,
      };

      if (button) {
        quiz.progressButton = button;
      }

      newState.slideReadyStates[activeSlideIndex] =
        isValidActivity(activeSlide);
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.TOGGLE_SHUFFLE_ANSWERS: {
      const newState = cloneDeep(state);
      const {activeSlide} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );

      const quiz = activeSlide.params.interaction.params;
      quiz.shuffleAnswers = !quiz.shuffleAnswers;
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.EDIT_QUESTION_IMAGE: {
      const newState = cloneDeep(state);
      const {activeSlide, activeSlideIndex} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );

      const interaction = activeSlide.params.interaction;
      const {
        urls: existingUrls,
        url: existingUrl,
        thumbnailUrls: existingThumbnailUrls,
        thumbnailUrlsCkey: existingThumbnailUrlsCkey,
        thumbnailUrl: existingThumbnailUrl,
        ...existingParams
      } = interaction.params;

      const {questionIndex, url, thumbnailUrl} = action.payload;

      const updateArray = (array, index, value) => {
        const updatedArray = [...array];
        updatedArray[index] = value;
        return updatedArray;
      };

      let updatedParams;

      if (existingUrls) {
        const arrayOfNulls = Array(existingParams.questions.length).fill(null);

        updatedParams = {
          urls: updateArray(existingUrls, questionIndex, url),
          thumbnailUrls: updateArray(
            // existingThumbnailUrls might be undefined for old lessons
            existingThumbnailUrls || arrayOfNulls,
            questionIndex,
            thumbnailUrl
          ),
          thumbnailUrlsCkey: existingThumbnailUrlsCkey
            ? existingThumbnailUrlsCkey
            : arrayOfNulls,
        };
      } else if (url !== existingUrl) {
        // promote from url to urls
        const updatedUrls = updateArray(
          Array(existingParams.questions.length).fill(existingUrl),
          questionIndex,
          url
        );
        const updatedThumbnailUrls = updateArray(
          Array(existingParams.questions.length).fill(
            existingThumbnailUrl || null
          ),
          questionIndex,
          thumbnailUrl
        );
        updatedParams = {
          urls: updatedUrls,
          thumbnailUrls: updatedThumbnailUrls,
          url: undefined,
          urlCkey: undefined,
          thumbnailUrl: undefined,
          thumbnailUrlCkey: undefined,
        };
      } else {
        updatedParams = {url, thumbnailUrl};
      }
      interaction.params = {...existingParams, ...updatedParams};

      newState.slideReadyStates[activeSlideIndex] =
        isValidActivity(activeSlide);
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.ADD_QUESTION: {
      const {slideIndex, defaultPrompt} = action.payload;
      const newState = cloneDeep(state);
      const slide = newState.slides[slideIndex];
      const slideParams = slide.params.interaction.params;

      //add empty image to new slide
      const generateUrlsArray = (param) => {
        if (slideParams[param]) {
          slideParams[param].push('');
        } else {
          const imageInput = slideParams.url ? slideParams.url : '';
          slideParams.questions.forEach(() => {
            if (slideParams[param]) {
              slideParams[param].push(imageInput);
            } else {
              slideParams[param] = [imageInput];
            }
          });
          slideParams[param].push('');

          slideParams.url = undefined;
          slideParams.urlCkey = undefined;
          slideParams.thumbnailUrl = undefined;
          slideParams.thumbnailUrlCkey = undefined;
        }
      };
      generateUrlsArray('urls');
      generateUrlsArray('thumbnailUrls');

      const quiz = slide.params.interaction.params;

      quiz.questions = [
        ...quiz.questions,
        quiz.quizType !== 'mixed'
          ? emptyQuestion
          : createMixedQuizMultipleChoiceQuestion(defaultPrompt),
      ];

      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.slideReadyStates[slideIndex] = isValidActivity(slide);

      // Switch to the new question
      const indexOfFirstElementOfNextSlide =
        getFirstSlideElementIndexWithSlideIndex(
          newState.slideElements,
          slideIndex + 1
        );
      const indexOfNewQuestion =
        indexOfFirstElementOfNextSlide === -1
          ? newState.slideElements.length - 2
          : indexOfFirstElementOfNextSlide - 2;
      newState.activeSlideElementIndex = indexOfNewQuestion;
      newState.deleteSlideIndex = -1;
      newState.deleteQuestionIndex = -1;
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.SHOW_DELETE_QUESTION_WARNING: {
      return {
        ...state,
        deleteQuestionIndex: action.payload,
      };
    }
    case actionTypes.HIDE_DELETE_QUESTION_WARNING: {
      return {
        ...state,
        deleteQuestionIndex: -1,
      };
    }
    case actionTypes.DELETE_QUESTION: {
      const newState = cloneDeep(state);
      const {activeSlide, activeSlideIndex} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );

      const quiz = activeSlide.params.interaction.params;
      quiz.questions.splice(newState.deleteQuestionIndex, 1);
      if (quiz.urls) {
        quiz.urls.splice(newState.deleteQuestionIndex, 1);
      }
      if (quiz.thumbnailUrls) {
        quiz.thumbnailUrls.splice(newState.deleteQuestionIndex, 1);
      }
      if (quiz.urlsCkey) {
        quiz.urlsCkey.splice(newState.deleteQuestionIndex, 1);
      }
      if (quiz.thumbnailUrlsCkey) {
        quiz.thumbnailUrlsCkey.splice(newState.deleteQuestionIndex, 1);
      }

      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.slideReadyStates[activeSlideIndex] =
        isValidActivity(activeSlide);

      newState.activeSlideElementIndex--; // Switch to previous element
      newState.deleteQuestionIndex = -1;
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.EDIT_RESULTS_SLIDE: {
      const newState = cloneDeep(state);
      const slideIndex = state.slides.length - 1;
      const params = merge(newState.slides[slideIndex].params, action.payload);

      newState.slides[slideIndex].params = params;
      newState.slideReadyStates[slideIndex] = isValidResults(
        params,
        state.minimumScore
      );
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.SET_MINIMUM_SCORE: {
      if (state.editingType !== EDITING_TYPES.FULL) {
        return state;
      }

      const newState = cloneDeep(state);
      const resultsSlideIndex = state.slides.length - 1;

      newState.minimumScore = action.payload;
      newState.slides[resultsSlideIndex].minimumScore = action.payload;
      newState.slideReadyStates[resultsSlideIndex] = isValidResults(
        state.slides[resultsSlideIndex].params,
        action.payload
      );
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.SET_EDITING_PASS_FAIL: {
      const newState = cloneDeep(state);
      const resultsSlideIndex = state.slides.length - 1;
      newState.slides[resultsSlideIndex].isPassing = action.payload;
      return newState;
    }
    case actionTypes.CHANGES_SUBMITTED: {
      return {
        ...cloneDeep(state),
        unsavedChanges: false,
      };
    }
    case actionTypes.UPDATE_ESON: {
      const {result, hasDiff} = action.payload;
      if (!hasDiff) {
        return state;
      }

      const newState = fromEsonToState(
        {eson: result.content},
        false,
        state.activeSlideElementIndex
      );
      return newState;
    }
    case actionTypes.MOVE_OVERLAY_ELEMENT: {
      const object = action.payload;

      const newState = cloneDeep(state);
      const {activeSlide} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );

      const currentElements = activeSlide.overlayElements || [];

      if (currentElements.length === 0) {
        return state;
      }

      const updatedElements = setOverlayElementToTop(
        currentElements,
        object.type,
        object
      );

      if (updatedElements === currentElements) {
        return state;
      }

      activeSlide.overlayElements = updatedElements;
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.REMOVE_OVERLAY_ELEMENT: {
      const object = action.payload;

      const newState = cloneDeep(state);

      const {activeSlide, activeSlideIndex} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );
      const objectType = object.type;

      const currentOverlayElements = activeSlide.overlayElements || [];
      const remainingElements = currentOverlayElements.filter(
        (layer) => layer.type !== objectType
      );

      if (remainingElements.length === currentOverlayElements.length) {
        return state;
      }
      activeSlide.overlayElements =
        normaliseOverlayElementOrder(remainingElements);
      newState.unsavedChanges = true;

      newState.slideReadyStates[activeSlideIndex] =
        isValidActivity(activeSlide);
      return newState;
    }
    case actionTypes.REORDER_OVERLAY_ELEMENT: {
      const object = action.payload;
      const objectType = object.type;

      const newState = cloneDeep(state);
      const {activeSlide} = getActiveSlide(
        newState.slides,
        newState.slideElements,
        newState.activeSlideElementIndex
      );

      const currentOverlayElements = activeSlide.overlayElements || [];

      if (currentOverlayElements.length <= 1) {
        return state;
      }

      const updatedElements = setOverlayElementToTop(
        currentOverlayElements,
        objectType
      );

      if (updatedElements === currentOverlayElements) {
        return state;
      }

      activeSlide.overlayElements = updatedElements;
      newState.unsavedChanges = true;

      return newState;
    }
    case actionTypes.REPLACE_SLIDES: {
      const newState = cloneDeep(state);
      const incomingSlides = cloneDeep(action.payload).map((s) => ({
        ...s,
        id: s.id ?? iterator.next().value,
      }));

      newState.slides = incomingSlides;
      newState.slideElements = newState.slides.map(getSlideElements).flat();
      newState.slideReadyStates = newState.slides.map(validateSlideByType);
      newState.unsavedChanges = true;

      if (newState.slides.length === 0) {
        newState.activeSlideElementIndex = 0;
        return newState;
      }

      const prevActiveSlideIndex = getActiveSlideIndex(
        state.slideElements,
        state.activeSlideElementIndex
      );
      const prevActiveSlideId = state.slides[prevActiveSlideIndex]?.id;

      if (typeof prevActiveSlideId === 'number') {
        const indexOfPrevActiveSlideInNewSlides = newState.slides.findIndex(
          (s) => s.id === prevActiveSlideId
        );
        const prevActiveSlideIsInNewSlides =
          indexOfPrevActiveSlideInNewSlides !== -1;

        if (prevActiveSlideIsInNewSlides) {
          const elementsForSlideInNewState = newState.slideElements.filter(
            ({ephemeralClientSideSlideId}) =>
              ephemeralClientSideSlideId === prevActiveSlideId
          );
          const numberOfElementsForSlideInNewState =
            elementsForSlideInNewState.length;
          const prevActiveSlideFirstElementIndex =
            getFirstSlideElementIndexWithSlideIndex(
              state.slideElements,
              prevActiveSlideIndex
            );
          const prevActiveSlideElementIndexOffset =
            state.activeSlideElementIndex - prevActiveSlideFirstElementIndex;
          const activeSlideElementIndexOffset = Math.min(
            prevActiveSlideElementIndexOffset,
            numberOfElementsForSlideInNewState - 1
          );

          newState.activeSlideElementIndex =
            getFirstSlideElementIndexWithSlideIndex(
              newState.slideElements,
              indexOfPrevActiveSlideInNewSlides
            ) + activeSlideElementIndexOffset;

          return newState;
        }
      }

      newState.activeSlideElementIndex = Math.min(
        newState.activeSlideElementIndex,
        newState.slideElements.length - 1
      );
      return newState;
    }
    default:
      return state;
  }
};
